import type { PortalLocale } from '@worldcoo/monolith/portals';
import { useRouteContext, useRouter } from '@tanstack/react-router';
import { i18n, setupI18n, type I18n } from '@lingui/core';
import { getRequestContext } from './data';
import { updateRouterLocale } from '@/router';
import { buildLanguagePath } from './language-path';

export function useTranslation() {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const router = useRouter();
	// NGO admin routes (`ngos.*` host) are not locale-prefixed — the router output
	// rewrite explicitly strips the prefix for `prefix === 'ngos'`. Pushing
	// `/<lang>/...` would land users on a non-existent route.
	const isNgoAdmin = portalId === 'ngos';

	const changeLanguage = (lang: PortalLocale, currentCampaignId?: string) => {
		loadLandingTranslation(lang);
		if (typeof document !== 'undefined') {
			document.cookie = `language=${lang}; path=/; max-age=31536000`;
		}
		updateRouterLocale(lang);
		if (router.options.context) {
			(router.options.context as { locale: PortalLocale }).locale = lang;
		}

		if (typeof window !== 'undefined' && !isNgoAdmin) {
			const newPath = buildLanguagePath(
				lang,
				window.location.pathname,
				window.location.search,
				locale,
				portalId,
				router,
				currentCampaignId,
			);
			router.history.push(newPath);
		}

		// On the admin app there's no URL change to trigger a re-render, so invalidate
		// the router so loaders and components pick up the new locale.
		if (isNgoAdmin) {
			router.invalidate();
		}

		getRequestContext({ data: lang }).then((ctx) => {
			if (router.options.context) {
				Object.assign(router.options.context, ctx);
			}
		});
	};

	return { language: locale, changeLanguage };
}

/**
 * Compute the href for each locale option.
 * Used to render language switcher items as real `<a>` links for preloading and SEO.
 */
export function useLanguageHrefs(locales: readonly PortalLocale[]): Record<string, string> {
	const { portalId, locale } = useRouteContext({ from: '__root__' });
	const router = useRouter();

	// Use router.history.location (available on both server and client) instead of
	// window.location to avoid a server/client branch that causes hydration mismatches.
	const { pathname, search } = router.history.location;

	const hrefs: Record<string, string> = {};
	for (const lang of locales) {
		hrefs[lang] = buildLanguagePath(lang, pathname, search, locale, portalId, router);
	}
	return hrefs;
}

export const landingLocales = ['es', 'en', 'ca', 'it'] as const;
export type LandingLocale = (typeof landingLocales)[number];

/**
 * Create the Lingui instance for the current render.
 *
 * On the server the portals run in a long-lived cluster worker that serves many
 * concurrent requests, so a single shared `i18n` catalog is mutable global state:
 * interleaved async SSR renders clobber each other's active locale. A request for
 * `/es/...` emits `<html lang="es">` while a concurrent request flips the shared
 * catalog to another locale, so the body renders in the wrong language; the client
 * then hydrates from `document.documentElement.lang` ("es") and re-renders different
 * text, throwing a hydration mismatch (React #418 — seen almost only from crawlers
 * hitting mixed locales in bursts). Giving each server request its own instance means
 * its catalog can't be touched by another request.
 *
 * On the client a single shared instance is correct (single-threaded per session) and
 * keeps the module-level `i18n` import valid for code that still reads it directly.
 */
export function createLocaleI18n(): I18n {
	return typeof document === 'undefined' ? setupI18n() : i18n;
}

/**
 * Load and activate a locale's catalog on the instance that will render it, and only that one.
 *
 * On the client `createLocaleI18n` hands back the shared global, so passing it as `instance` (or
 * omitting it) activates the global exactly as before. On the server `instance` is request-scoped
 * and the global is deliberately left alone: it is process-wide state in a long-lived worker, so
 * activating it per request let concurrent renders clobber each other's locale (React #418). The
 * two remaining readers of the global — the ngo login submit handler and the translation toast —
 * only ever run on the client. See {@link createLocaleI18n}.
 */
export async function loadLandingTranslation(locale: PortalLocale, instance?: I18n) {
	const { messages } = await import(`../locales/${locale}.po`);
	const target = instance ?? i18n;
	target.loadAndActivate({ locale, messages });
	return target;
}
